import { Box, Button, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import ExportAssetsContent from './ExportAssetsContent';
import {
  type BackpopulateResult,
  useBackpopulateAllBranding,
  useBackpopulatePalette,
} from './hooks/useExportBranding';

const showBackpopulateResult = (successTitle: string, result?: BackpopulateResult) => {
  if (!result) return;

  notifications.show({
    title: successTitle,
    message: `Populated ${result.populated} tenant(s), skipped ${result.skipped}.`,
    color: 'green',
  });

  for (const err of result.errors) {
    notifications.show({
      title: `Failed: ${err.tenantName}`,
      message: err.error,
      color: 'red',
      autoClose: false,
    });
  }
};

export default function ExportAssetsTab() {
  const { backpopulateAll, isLoading } = useBackpopulateAllBranding();
  const { backpopulatePalette, isLoading: isPaletteLoading } = useBackpopulatePalette();

  const handleBackpopulate = async () => {
    try {
      const result = await backpopulateAll();
      showBackpopulateResult('Branding back-populated', result?.data);
    } catch (error) {
      notifyAndLogError(
        'Back-populate failed',
        'Failed to back-populate branding for all tenants.',
        error,
      );
    }
  };

  const handleBackpopulatePalette = async () => {
    try {
      const result = await backpopulatePalette();
      showBackpopulateResult('Brand colours back-populated', result?.data);
    } catch (error) {
      notifyAndLogError(
        'Back-populate failed',
        'Failed to back-populate brand colours for all tenants.',
        error,
      );
    }
  };

  return (
    <Stack gap="md">
      <Box p="md" bg="white">
        <Group justify="space-between" align="center">
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
            Parse existing export_assets.docx files and create branding configs for all tenants that
            don&apos;t have one yet. &ldquo;Backpopulate brand colours&rdquo; reads the primary,
            secondary and accent colours from each tenant&apos;s heading styles and overwrites their
            stored palette.
          </Text>
          <Group gap="sm">
            <Button onClick={handleBackpopulate} loading={isLoading} variant="light" color="orange">
              Backpopulate all tenants
            </Button>
            <Button
              onClick={handleBackpopulatePalette}
              loading={isPaletteLoading}
              variant="light"
              color="orange"
            >
              Backpopulate brand colours
            </Button>
          </Group>
        </Group>
      </Box>
      <CompanyWrapper contentComponent={ExportAssetsContent} />
    </Stack>
  );
}
